import React from 'react';
import { ChevronUpIcon, EllipsisIcon, PencilIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Badge } from '@rocco/components/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { useNotifications } from '~/modules/notifications';
import { useChat } from '../../hooks';

/**
 * The floating chat inbox bubble which opens the chat inbox drawer.
 * @see {@link ChatInboxDrawer}
 */
export const ChatInboxFloatingButton = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<'div'>
>(function ChatInboxFloatingButton({ className, ...divProps }, ref) {
    const { setChatOpenState, openChatBox } = useChat();
    const { notifications } = useNotifications();

    return (
        <div
            ref={ref}
            className={cn(
                'flex w-92 items-center gap-1 rounded-full bg-black/25 px-5 py-2 text-white shadow-[-0.5px_-1px_1px_0] shadow-white backdrop-blur-lg hover:bg-black/40',
                className
            )}
            role="button"
            aria-label="My Messages"
            {...divProps}
        >
            <Badge
                className={cn(!notifications.userInbox?.count && 'hidden')}
                size="centeredCompact"
                color="red"
            >
                {notifications.userInbox?.count
                    ? notifications.userInbox.count > 9
                        ? '9+'
                        : notifications.userInbox.count
                    : '0'}
            </Badge>
            <div className="ml-2 min-w-0 flex-1 truncate text-4.5 leading-tight">
                {notifications.userInbox?.count
                    ? 'Unread Messages'
                    : 'My Messages'}
            </div>
            <Popover>
                <PopoverTrigger
                    className="flex size-10 items-center justify-center rounded-full hover:bg-white/20"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisIcon className="size-6" />
                </PopoverTrigger>
                <PopoverContent
                    className="min-w-32 overflow-hidden rounded-lg bg-gray-100 p-0 before:rounded-lg"
                    side="top"
                    sideOffset={12}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ul className="flex flex-col text-4 leading-tight tracking-wide">
                        <li
                            className="p-2 hover:bg-gray-200"
                            role="button"
                            onClick={() => setChatOpenState('hidden')}
                        >
                            Hide
                        </li>
                    </ul>
                </PopoverContent>
            </Popover>
            <button
                className="flex size-10 items-center justify-center rounded-full hover:bg-white/20"
                aria-label="New Message"
                onClick={(e) => {
                    e.stopPropagation();
                    setChatOpenState('open');
                    openChatBox(0);
                }}
            >
                <PencilIcon className="size-5.5" />
            </button>
            <ChevronUpIcon className="size-7" />
        </div>
    );
});
